import moment from 'moment';
import { BasePage } from './../base.page';
import { ChatModel } from 'src/app/models/chat.model';
import { TranslateService } from '@ngx-translate/core';
import { debounceTime, finalize } from 'rxjs/operators';
import { ActivatedRoute, Router } from '@angular/router';
import { ChatService } from './../../services/chat.service';
import { UserService } from 'src/app/services/user.service';
import { AuthService } from 'src/app/services/auth.service';
import { Component, OnInit, ViewChild } from '@angular/core';
import { PusherService } from './../../services/pusher.service';
import { GeneralService } from './../../services/general.service';
import { ErrorHandlerService } from 'src/app/services/error-handler.service';
import { NotificationsCountModel } from 'src/app/models/notifications.model';
import {
  PaginatedResponse,
  ListingParamsModel,
} from 'src/app/models/paginated-response.model';
import { HamburgerMenuComponent } from 'src/app/components/hamburger-menu/hamburger-menu.component';
import {
  Platform,
  LoadingController,
  ToastController,
  IonInfiniteScroll,
  ModalController,
} from '@ionic/angular';

@Component({
  selector: 'shc-chat',
  templateUrl: './chat.page.html',
  styleUrls: ['./chat.page.scss'],
})
export class ChatPage extends BasePage implements OnInit {
  page: number;
  type: string;
  segmentCurrent: string;
  callInProgress: boolean;
  statuses = ['accept', 'pending'];
  chats: PaginatedResponse<ChatModel>;
  loadingMoreData = false;
  notificationCount: NotificationsCountModel;
  params: ListingParamsModel = new ListingParamsModel();
  @ViewChild(IonInfiniteScroll)
  private readonly infiniteScroll: IonInfiniteScroll;

  constructor(
    protected platform: Platform,
    protected userService: UserService,
    protected authService: AuthService,
    protected translate: TranslateService,
    private errorService: ErrorHandlerService,
    protected loadingCtrl: LoadingController,
    toastController: ToastController,
    private router: Router,
    private route: ActivatedRoute,
    private generalService: GeneralService,
    private chatService: ChatService,
    private pusherService: PusherService,
    private modalCtrl: ModalController,
  ) {
    super(
      platform,
      userService,
      authService,
      translate,
      loadingCtrl,
      toastController,
    );
    this.route.params.subscribe((res) => {
      this.initChat();
    });
  }

  ngOnInit() {}

  ionViewWillEnter() {}

  initChat() {
    this.callInProgress = true;
    this.segmentCurrent = 'all';
    this.params.page = 1;
    this.getChats();
  }

  segmentChanged(ev) {
    if (this.callInProgress) {
      return;
    }
    this.segmentCurrent = ev.target.value;
    this.getChats();
  }

  getChats(event?: any) {
    this.generalService.getLoggedInUser(this.user$).then(
      (data: any) => {
        this.user = data;
        if (event) {
          this.setListParams(event);
        }
        if (this.segmentCurrent === 'all') {
          this.params.type = '';
        } else {
          this.params.type = this.segmentCurrent;
        }

        this.params.statuses = this.statuses;

        if (!this.loadingMoreData) {
          this.showLoader();
        }

        this.chatService
          .get(this.user.id, this.params)
          .pipe(
            finalize(() => {
              if (!this.loadingMoreData) {
                this.hideLoader();
              } else {
                this.loadingMoreData = false;
              }
            }),
          )
          .subscribe(
            (chats: any) => {
              // this.hideLoader();
              this.callInProgress = false;
              chats.data = chats.data.filter((item) => item.message);
              // chats.data.map((item: ChatModel) => item.updated_at = moment(item.updated_at).format("hh:mm a"));
              if (this.chats && this.chats.meta) {
                if (this.params.page === 1) {
                  this.chats = chats;
                } else {
                  this.chats.meta = chats.meta;
                  this.chats.data = [...this.chats.data, ...chats.data];
                }
              } else {
                this.chats = chats;
              }
              this.bindAllUsers();
              this.params.page++;
              if (event) {
                event.target.complete();
              }
            },
            (error) => {
              // this.hideLoader();
              this.callInProgress = false;
              this.errorService.handle(error);
            },
          );
      },
      (error) => {
        this.hideLoader();
        this.callInProgress = false;
        this.errorService.handle(error);
      },
    );
  }

  setListParams(event) {
    if (typeof event.target.value === 'string') {
      this.params.search = event.target.value;
      this.params.page = null;
    }
  }

  bindAllUsers() {
    this.pusherService.refreshChat(this.user.id);
    /**
     * If new message is received in any of the chat then update that chat last message on this screen
     */
    this.pusherService
      .getRefresh()
      .pipe(debounceTime(500))
      .subscribe((response) => {
        if (response.chat_id) {
          /**
           * Get this specific chat messages
           */
          this.chatService
            .getChatDetail(response.chat_id)
            .subscribe((res: any) => {
              /**
               * Get the chat index that needs to be updated
               */
              const updateChatIndex = this.chats.data.findIndex(
                (chat) => chat.id === res.id,
              );

              /**
               * Update Chat Data if there's any chat
               * else add new chat
               */
              if (updateChatIndex !== -1) {
                this.chats.data[updateChatIndex] = res;
              } else if (updateChatIndex === -1) {
                this.chats.data.push(res);
              }
            });
        }
      });
  }

  unBindAllUsers() {
    this.pusherService.unBindRefreshEvent(this.user.id);
  }

  /**
   * Open the chat detail
   * @param param single rec of chat data
   */
  openChatDetail(item) {
    if (item.chat_request.status === 'pending') {
      this.translate.get('Chat.NotAContact').subscribe((message) => {
        this.showToast(message);
      });
      return;
    }
    const chatIndex = this.chats.data.findIndex((chat) => chat.id === item.id);
    // this.chats.data[chatIndex].chat_request.user.unread_messages_count = 0;
    item.user_id = item.id;
    this.router.navigate(['chat-detail', item.id]);
  }

  removeChat(chat) {
    this.showLoader();
    this.chatService.removeChat(chat.id).subscribe(
      (res) => {
        this.hideLoader();
        const removeIndex = this.chats.data
          .map((item) => item.id)
          .indexOf(chat.id);
        if (removeIndex !== 1) {
          this.chats.data.splice(removeIndex, 1);
          this.chats.data = [...this.chats.data];
        }
      },
      (error) => {
        this.hideLoader();
        this.errorService.handle(error);
      },
    );
  }

  getTimeFromDate(date: string) {
    const momentDate = moment(date);
    if (momentDate.isSame(moment(), 'day')) {
      return momentDate.format('HH:mm');
    }
    if (momentDate.isSame(moment(), 'week')) {
      return momentDate.format('dddd');
    }
    return momentDate.format('DD MMM YYYY');
  }

  navigateToChat(id: string) {
    this.router.navigate(['chat', id]);
  }

  async openMenu() {
    const modal = await this.modalCtrl.create({
      component: HamburgerMenuComponent,
      cssClass: 'hamBurgermenu',
      canDismiss: true,
      id: 'hamBurgermenu',
      // initialBreakpoint: 0.9
    });
    modal.onDidDismiss().then((data) => {
      this.modalCtrl.dismiss();
    });
    return await modal.present();
  }

  loadData(event): void {
    if (this.chats.meta.current_page < this.chats.meta.last_page) {
      this.loadingMoreData = true;
      this.getChats(event);
    } else {
      this.infiniteScroll.disabled = true;
    }
  }
}
